import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';
import API from '../lib/api';
import { LayoutDashboard, FolderKanban, BookOpen, Building2, LogOut, ChevronRight, Globe, Bell, Lightbulb, Target, Brain, Radar, BarChart3, Network, GitCompareArrows, UserCog, PanelsTopLeft, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import ManualDrawer from './ManualDrawer';

// Single-entity SAGA navigation. Admin sees extra items (Company Profile + Users).
const BASE_NAV = [
  { path: '/dashboard', label: 'Panou de Control', icon: LayoutDashboard },
  { path: '/projects', label: 'Licitatii', icon: FolderKanban },
  { path: '/knowledge', label: 'Baza Cunostinte ML', icon: Brain },
  { path: '/comparator', label: 'Comparator Cerinte', icon: GitCompareArrows },
  { path: '/seap-radar', label: 'Radar SEAP', icon: Radar },
  { path: '/seap', label: 'Cautare SEAP', icon: Globe },
  { path: '/competitors', label: 'Competitor Intelligence', icon: Target },
  { path: '/reports', label: 'Rapoarte', icon: BarChart3 },
  { path: '/escalation', label: 'Escaladare', icon: Network },
  { path: '/lessons', label: 'Lecții Învățate', icon: Lightbulb },
  { path: '/library', label: 'Biblioteca Raspunsuri', icon: BookOpen },
];
const ADMIN_NAV = [
  { path: '/company', label: 'Profil Companie', icon: Building2 },
  { path: '/admin/users', label: 'Utilizatori', icon: UserCog },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    API.get('/notifications').then(r => setNotifications(r.data)).catch(() => {});
    const interval = setInterval(() => {
      API.get('/notifications').then(r => setNotifications(r.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const isAdmin = !!user?.is_admin;
  const navItems = [
    ...(user?.erp_permissions?.includes('erp.access') ? [{ path: '/erp', label: 'Operațiuni ERP', icon: PanelsTopLeft }] : []),
    ...BASE_NAV,
    ...(isAdmin ? ADMIN_NAV : []),
  ];
  const urgentCount = notifications.filter(n => n.priority === 'high').length;

  const moduleLabel = isAdmin ? 'Administrator' : 'Ofertare';

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      <div className="md:hidden flex items-center justify-between p-3 border-b border-border"><img src="/saga-icon.png" alt="SAGA" className="w-9 h-9 object-contain" /><span className="font-semibold">SAGA Infrastructura</span><Button variant="ghost" size="icon" aria-label={mobileOpen ? 'Închide meniul' : 'Deschide meniul'} aria-expanded={mobileOpen} aria-controls="saga-sidebar" onClick={() => setMobileOpen(v => !v)} data-testid="mobile-menu-toggle">{mobileOpen ? <X /> : <Menu />}</Button></div>
      <aside id="saga-sidebar" data-testid="saga-sidebar" className={`${mobileOpen ? 'flex' : 'hidden'} md:flex w-full md:w-64 border-r border-border flex-col bg-card shrink-0 md:sticky md:top-0 md:h-screen md:overflow-y-auto`}>
        <div className="p-5 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
            <img src="/logo-saga.gif" alt="SAGA" className="h-9 object-contain" />
            <div>
              <h1 className="font-bold text-base leading-tight" style={{ fontFamily: 'Outfit' }}>SAGA</h1>
              <p className="text-[10px] text-muted-foreground">Infrastructura</p>
            </div>
          </Link>
          <Badge variant="outline" className="mt-2 text-[9px] bg-primary/5 text-primary/80 ring-primary/20 ring-1 ring-inset border-0">
            {moduleLabel}
          </Badge>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => {
            const isActive = location.pathname === item.path ||
              (item.path === '/projects' && location.pathname.startsWith('/projects')) ||
              (item.path === '/erp' && location.pathname.startsWith('/erp'));
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                }`}
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
                {isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
              </Link>
            );
          })}
          <button
            onClick={() => setManualOpen(true)}
            data-testid="nav-manual"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-white/5 hover:text-foreground transition-all duration-200"
          >
            <BookOpen className="w-4 h-4" strokeWidth={1.5} />
            Manual
          </button>
        </nav>

        <div className="px-4 pb-2">
          <Popover>
            <PopoverTrigger asChild>
              <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-white/5 hover:text-foreground transition-all" data-testid="notification-bell">
                <Bell className="w-4 h-4" strokeWidth={1.5} />
                Notificari
                {urgentCount > 0 && (
                  <Badge className="ml-auto bg-rose-500 text-white text-[10px] px-1.5 py-0 h-5">{urgentCount}</Badge>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 bg-zinc-950 border-white/10 p-0" side="right" align="end">
              <div className="p-3 border-b border-white/10">
                <p className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>Notificari ({notifications.length})</p>
              </div>
              <div className="max-h-64 overflow-auto">
                {notifications.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">Nicio notificare</p>
                ) : notifications.map((n, i) => (
                  <Link key={i} to={n.project_id ? `/projects/${n.project_id}` : '#'}
                    className="flex items-start gap-3 p-3 border-b border-white/5 hover:bg-white/5 transition-colors">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.priority === 'high' ? 'bg-rose-400' : n.priority === 'medium' ? 'bg-amber-400' : 'bg-blue-400'}`} />
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{n.title}</p>
                      <p className="text-[10px] text-muted-foreground">{n.message}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium text-primary">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.company_name || user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost" size="sm" onClick={logout} data-testid="logout-btn"
            className="w-full justify-start text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Deconectare
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
      <ManualDrawer open={manualOpen} onOpenChange={setManualOpen} />
    </div>
  );
}
